// Ported from the onboarding prototype (gs-onboarding, 14 Oct 2026). The scene
// layout is illustrative; only the supplied pictures are the user's own.

export type SceneMedia = {
  id: string;
  kind: 'photo' | 'video';
  title: string;
  image: string | null;
  duration: string;
  // An illustrated example standing in for a Vault without pictures.
  sample?: boolean;
};

export type SceneRecipient = { firstName: string; avatarUrl: string | null };

type ArchiveKey =
  | 'summers'
  | 'postcard'
  | 'jump'
  | 'log'
  | 'video'
  | 'deed'
  | 'again'
  | 'audio';

export const ARCHIVE: { key: ArchiveKey; type: string }[] = [
  { key: 'summers', type: 'Photograph' },
  { key: 'postcard', type: 'Letter' },
  { key: 'jump', type: 'Video' },
  { key: 'log', type: 'Document' },
  { key: 'video', type: 'Video' },
  { key: 'deed', type: 'Record' },
  { key: 'again', type: 'Photograph' },
  { key: 'audio', type: 'Audio' },
];

// The user's pictures fill these slots, in this order (the welcome cards'
// order); the remaining slots stay illustrative.
export const MEDIA_SLOTS: ArchiveKey[] = ['jump', 'video', 'summers', 'again'];

export type SceneCopy = { id: string; title: string; subtitle: string };

// Preserve, vault, timeline. Their copy depends on the variant (see
// onboarding-copy.ts).
export const SCENE_COUNT = 3;

const escape = (value: unknown) =>
  String(value ?? '').replace(
    /[&<>"']/g,
    (char) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[
        char
      ] as string,
  );
const imageURL = (value: unknown) =>
  typeof value === 'string' &&
  /^(?:\/(?!\/)|\.\.?\/|https?:\/\/|blob:https?:\/\/|data:image\/(?:png|jpeg|webp|gif);base64,)/i.test(
    value,
  )
    ? value
    : '';
const skeleton = (kind = 'title') =>
  `<span class="scene-skeleton scene-skeleton-${kind}" aria-hidden="true"></span>`;
const ICONS: Record<string, string> = {
  grid: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>',
  photo:
    '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8" cy="8" r="1.5"/><path d="m3 17 5-5 4 4 3-3 6 6"/>',
  video:
    '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m10 9 5 3-5 3Z"/>',
  audio:
    '<rect x="9" y="2" width="6" height="13" rx="3"/><path d="M6 11v1a6 6 0 0 0 12 0v-1M12 18v4m-3 0h6"/>',
  document: '<path d="M14 2H5v20h14V7ZM14 2v5h5M8 11h8M8 15h8M8 18h5"/>',
  note: '<path d="M5 3h14v18H5zM8 8h8M8 12h8M8 16h5"/>',
  person: '<circle cx="12" cy="8" r="3"/><path d="M5 21v-2a7 7 0 0 1 14 0v2"/>',
  place:
    '<path d="M19 10c0 5-7 12-7 12S5 15 5 10a7 7 0 1 1 14 0Z"/><circle cx="12" cy="9" r="2"/>',
};
const icon = (kind: string) =>
  `<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[kind] ?? ''}</svg>`;
const svg = (viewBox: string, contents: string) =>
  `<svg class="scene-object-art" viewBox="${viewBox}" fill="none" aria-hidden="true">${contents}</svg>`;
const paperLines = (
  start: number,
  widths: number[],
  x = 28,
  gap = 22,
  color = '#dddcd3',
) =>
  widths
    .map(
      (w, index) =>
        `<path d="M${x} ${start + index * gap}h${w}" stroke="${color}" stroke-width="5" stroke-linecap="round"/>`,
    )
    .join('');
const documentArt = () =>
  svg(
    '0 0 240 308',
    `<path d="M6 2h194l34 35v269H6Z" fill="#faf8f0" stroke="#dfdfd3" stroke-width="2"/><path d="M200 2v35h34" fill="#eeede4" stroke="#dfdfd3" stroke-width="2"/><rect x="27" y="34" width="33" height="40" rx="2" fill="#e6eae1"/><path d="M35 47h16M35 55h12M35 63h16" stroke="#b7c7bc" stroke-width="2"/>${paperLines(
      104,
      [152, 174, 160, 174, 117, 156],
      28,
      24,
    )}<path d="M29 270h68" stroke="#b9c8be" stroke-width="3" stroke-linecap="round"/>`,
  );
const letterArt = () =>
  svg(
    '0 0 310 200',
    `<rect x="4" y="25" width="302" height="171" rx="3" fill="#eae3d5" stroke="#d6cebd"/><path d="m5 26 150 111L305 26M5 196l108-89m193 89-107-89" stroke="#d4cbbc" stroke-width="1.5"/><rect x="15" y="5" width="280" height="166" rx="2" fill="#faf7ef" stroke="#ded8c9"/><rect x="250" y="19" width="26" height="33" rx="1" fill="#e5e9df" stroke="#c3cbbb" stroke-dasharray="2 2"/><path d="M263 46V28m0 9-6-5m6 10 6-7" stroke="#849b88" stroke-width="1.5"/>${paperLines(
      37,
      [89, 166, 207, 196, 133],
      36,
      23,
      '#d9d7cb',
    )}`,
  );
const recordArt = () =>
  svg(
    '0 0 228 300',
    `<rect x="3" y="3" width="222" height="294" rx="2" fill="#f6f4eb" stroke="#d9d9ca" stroke-width="2"/><rect x="14" y="14" width="200" height="272" rx="1" stroke="#d8dece"/><path d="M80 45h70" stroke="#b8c4b2" stroke-width="7" stroke-linecap="round"/>${paperLines(
      83,
      [146, 164, 127, 157, 99],
      32,
      25,
    )}<circle cx="170" cy="252" r="19" fill="#e6dfce"/><circle cx="170" cy="252" r="13" stroke="#c7bfae"/><path d="M36 252h57" stroke="#b9c5b5" stroke-width="3" stroke-linecap="round"/>`,
  );
const wave = [
  22, 38, 56, 31, 69, 84, 48, 28, 61, 96, 70, 44, 26, 55, 82, 39, 66, 48, 29,
  57, 36, 20, 47, 62, 33, 18,
];
const audioArt = () =>
  `<div class="scene-audio-card"><span class="scene-audio-file">${icon('audio')}<span>Recording.m4a</span></span><span class="scene-audio-wave" aria-hidden="true">${wave.map((height) => `<i style="--wave-height:${height}%"></i>`).join('')}</span><span class="scene-audio-controls" aria-hidden="true"><span>▶</span><i></i></span></div>`;

// The film-frame artwork that stands in for a missing picture.
export const videoPlaceholderMarkup = () =>
  svg(
    '0 0 400 300',
    `<rect width="400" height="300" rx="8" fill="#e9eee7"/><rect x="21" y="21" width="358" height="258" rx="5" stroke="#cad6ca"/>${[
      44, 91, 138, 185, 232,
    ]
      .map(
        (y) =>
          `<rect x="30" y="${y}" width="10" height="19" rx="2" fill="#d4dfd2"/><rect x="360" y="${y}" width="10" height="19" rx="2" fill="#d4dfd2"/>`,
      )
      .join(
        '',
      )}<rect x="127" y="211" width="146" height="7" rx="3.5" fill="#c9d6c8"/><rect x="159" y="229" width="82" height="5" rx="2.5" fill="#d5dfd2"/>`,
  );

const mediaLabel = (memory: SceneMedia) =>
  memory.title || (memory.kind === 'video' ? 'Video recording' : 'Photo');

export function sceneMarkup(
  assetBase: string,
  {
    media = [],
    recipient,
    scenes,
  }: {
    media: (SceneMedia | null)[];
    recipient: SceneRecipient;
    scenes: SceneCopy[];
  },
) {
  const base = String(assetBase).replace(/\/$/, '');
  if (!/^(?:\/[^<>"']*|\.[^<>"']*|https?:\/\/[^<>"']*)$/.test(base))
    throw new Error('assetBase must be a safe URL directory');
  const memories: Partial<Record<ArchiveKey, SceneMedia>> = {};
  MEDIA_SLOTS.forEach((key, index) => {
    if (media[index]) memories[key] = media[index];
  });
  const firstName = String(recipient.firstName || '').trim();
  const avatar = imageURL(recipient.avatarUrl);
  const avatarMarkup = () =>
    `<i class="scene-recipient-avatar" data-recipient-avatar="true" aria-label="${escape(firstName || 'You')}">${avatar ? `<img src="${escape(avatar)}" alt=""/>` : ''}<span>${escape(firstName ? Array.from(firstName)[0].toUpperCase() : '') || icon('person')}</span></i>`;
  const placeholderAvatar = () =>
    `<i class="scene-placeholder-avatar" aria-label="Illustrative person">${icon('person')}</i>`;
  const person = () =>
    `<span class="vault-person-reference">${avatarMarkup()}${escape(firstName || 'You')}</span><span class="vault-person-reference">${placeholderAvatar()}${skeleton('person')}</span>`;
  const attrs = (key: ArchiveKey) => {
    const memory = memories[key];
    return `data-content-kind="${memory && !memory.sample ? 'existing' : 'illustrative'}"${memory && !memory.sample ? ` data-memory-id="${escape(memory.id)}" data-media-kind="${memory.kind}"` : ''}`;
  };
  const caption = (key: ArchiveKey) => {
    const memory = memories[key];
    if (!memory)
      return `<strong>${skeleton()}</strong><span class="vault-caption-meta">${skeleton('meta')}</span>`;
    const meta =
      memory.kind === 'video'
        ? `Video${memory.duration ? ` · ${escape(memory.duration)}` : ''}`
        : 'Photo';
    return `<strong>${escape(mediaLabel(memory))}</strong><span class="vault-caption-meta">${meta}</span>`;
  };
  // One of the user's own pictures: the poster over the film-frame artwork
  // (which shows if the image fails), with a play button for videos.
  const ownMedia = (memory: SceneMedia) => {
    const poster = imageURL(memory.image);
    const video = memory.kind === 'video';
    return `<div class="scene-video-content is-existing">${videoPlaceholderMarkup()}${poster ? `<img class="scene-video-poster" src="${escape(poster)}" alt="${escape(mediaLabel(memory))}"/>` : ''}${video ? '<span class="scene-video-play" aria-hidden="true">▶</span>' : ''}${video && memory.duration ? `<span class="scene-video-duration">${escape(memory.duration)}</span>` : ''}${memory.sample ? '' : `<span class="scene-video-own">${video ? 'Your recording' : 'Your photo'}</span>`}</div>`;
  };
  const contents = (key: ArchiveKey) => {
    const memory = memories[key];
    if (memory) return ownMedia(memory);
    if (key === 'jump' || key === 'video')
      return `<div class="scene-video-content">${videoPlaceholderMarkup()}<span class="scene-video-play" aria-hidden="true">▶</span></div>`;
    if (key === 'summers' || key === 'again')
      return `<img class="scene-watercolor-photo" src="${escape(base)}/illustrations/photo-watercolor.webp" alt="Watercolor illustration representing photographs"/>`;
    return {
      postcard: letterArt,
      log: documentArt,
      deed: recordArt,
      audio: audioArt,
    }[key as 'postcard' | 'log' | 'deed' | 'audio']();
  };
  const ariaLabel = (key: ArchiveKey) => {
    const memory = memories[key];
    if (memory?.sample)
      return `Illustrative ${memory.kind}: ${mediaLabel(memory)}`;
    if (memory)
      return `Your ${memory.kind === 'video' ? 'video recording' : 'photo'}: ${mediaLabel(memory)}`;
    return `Illustrative ${ARCHIVE.find((item) => item.key === key)!.type.toLowerCase()}`;
  };
  const existing = (key: ArchiveKey) =>
    memories[key] && !memories[key]!.sample ? ' is-existing' : '';
  const mediaSlot = (key: ArchiveKey) =>
    `<div class="${key === 'jump' ? 'photo-slot' : `vault-media vault-media-${key}`} scene-media scene-media-${key}${existing(key)}" ${key === 'jump' ? '' : `data-media="${key}"`} ${attrs(key)} role="img" aria-label="${escape(ariaLabel(key))}">${contents(key)}</div>`;
  const tile = (key: ArchiveKey, timeline = false) =>
    `<figure class="vault-tile vault-tile-${key}" data-tile="${key}" ${attrs(key)}>${timeline ? `<div class="vault-year">${skeleton('year')}</div>` : ''}${mediaSlot(key)}<figcaption>${caption(key)}</figcaption></figure>`;
  const header = () =>
    `<div class="vault-topbar"><img src="${escape(base)}/product-logo.svg" alt="Generational Story" width="467" height="71"/><div class="vault-members">${avatarMarkup()}${placeholderAvatar()}<span>${escape(firstName || 'You')}</span></div></div><div class="vault-navigation" role="group" aria-label="Illustrative product navigation"><span>Dashboard</span><span class="vault-timeline-tab">Timeline</span><span class="vault-album-tab">Albums</span><span class="vault-memories-tab">Memories</span></div><div class="vault-album-title"><div><h3>Your Vault</h3><p>${skeleton('meta')}</p></div></div>`;
  const categories = [
    ['grid', 'All memories'],
    ['photo', 'Photos'],
    ['video', 'Videos'],
    ['audio', 'Audio'],
    ['document', 'Documents'],
    ['note', 'Notes'],
    ['place', 'Places'],
    ['person', 'People'],
  ];
  const overview = () =>
    `<div class="vault-board vault-overview-board"><aside class="vault-category-sidebar" aria-label="Archive categories">${categories.map(([name, label], i) => `<span class="vault-category${i === 0 ? ' selected' : ''}">${icon(name)}${label}</span>`).join('')}</aside><div class="vault-overview-main"><div class="vault-overview-featured">${tile('jump')}${tile('log')}<div class="vault-overview-context">${tile('audio')}<aside class="vault-excerpt"><span class="vault-small-label">A memory in words</span><blockquote>${skeleton('quote')}${skeleton('quote-short')}</blockquote><span class="vault-excerpt-source">${skeleton('meta')}</span></aside><div class="vault-family-context"><div class="vault-people-context"><span class="vault-small-label">People</span><div>${person()}</div></div><div class="vault-place-context">${icon('place')}<div><span class="vault-small-label">Place</span><strong>${skeleton('person')}</strong></div></div></div></div></div><div class="vault-overview-strip">${(['summers', 'postcard', 'video', 'deed', 'again'] as ArchiveKey[]).map((key) => tile(key)).join('')}</div></div></div>`;
  const timeline = () =>
    `<div class="vault-board vault-timeline-board">${(['deed', 'summers', 'postcard', 'jump', 'video', 'again', 'audio'] as ArchiveKey[]).map((key) => tile(key, true)).join('')}</div>`;
  const solution = ARCHIVE.map(
    ({ key }) =>
      `<div class="gs-loose-item" data-loose="${key}">${mediaSlot(key)}</div>`,
  ).join('');
  const carriers = ARCHIVE.map(
    ({ key }) =>
      `<div class="${key === 'jump' ? 'photo-carrier' : `artifact-carrier artifact-carrier-${key}`}" data-carrier="${key}" ${attrs(key)} aria-hidden="true"><div class="carrier-image scene-media-${key}${existing(key)}">${contents(key)}</div><div class="carrier-caption">${caption(key)}</div></div>`,
  ).join('');
  return `<section class="gs-scene-copy gs-preserve-copy" data-copy="0"><h2 tabindex="-1">${escape(scenes[0].title)}</h2><p>${escape(scenes[0].subtitle)}</p></section><section class="gs-scene-copy gs-timeline-copy" data-copy="2"><h2 tabindex="-1">${escape(scenes[2].title)}</h2><p>${escape(scenes[2].subtitle)}</p></section><div class="gs-measurements"><div class="gs-frame-anchor" data-frame="album"><div class="vault-frame vault-album">${header()}${overview()}</div></div><div class="gs-frame-anchor" data-frame="timeline"><div class="vault-frame vault-timeline">${header()}${timeline()}</div></div></div><div class="gs-solution-layout" aria-hidden="true">${solution}</div><div class="gs-shared-visuals" aria-hidden="true"><div class="product-shell-carrier">${header()}<div class="timeline-selection"></div><div class="shared-overview-content">${overview()}</div><div class="shared-timeline-content">${timeline()}</div></div>${carriers}</div>`;
}
